import * as React from 'react';
import { SidebarSearchModal } from '@backstage/plugin-search';

/**
 * Custom wrapper for SidebarSearchModal that ensures scrollbars are properly restored
 * after modal interactions and handles all modal-related UI fixes locally.
 * 
 * @remarks
 * This component handles:
 * - Scrollbar preservation after modal close
 * - Body padding reset prevention
 * - Grid container hiding in search modals
 * - Modal backdrop positioning
 */
export const CustomSidebarSearchModal = () => {
  React.useEffect(() => {
    // Store original styles for restoration
    const originalStyles = {
      bodyOverflow: document.body.style.overflow || 'auto',
      bodyPaddingRight: document.body.style.paddingRight || '0px',
      htmlOverflow: document.documentElement.style.overflow || 'auto',
    };

    /**
     * Comprehensive function to preserve scrollbars and apply modal fixes
     * Handles all UI/UX issues related to modal interactions
     */
    const preserveScrollbarsAndApplyFixes = () => {
      // Force scrollbar preservation on body and html
      document.body.style.setProperty('overflow', 'auto', 'important');
      document.documentElement.style.setProperty('overflow', 'auto', 'important');
      
      // Remove any padding-right that modals might add
      document.body.style.setProperty('padding-right', '0px', 'important');
      
      // Ensure modal backdrop doesn't interfere with scrolling
      const backdrops = document.querySelectorAll('.MuiBackdrop-root');
      backdrops.forEach(backdrop => {
        (backdrop as HTMLElement).style.setProperty('position', 'fixed', 'important');
      });
      
      // Hide MuiGrid-container in any open search modal
      const searchModals = document.querySelectorAll('[role="dialog"], .MuiDialog-root, .MuiModal-root');
      searchModals.forEach(modal => {
        const gridContainers = modal.querySelectorAll('.MuiGrid-container');
        gridContainers.forEach(grid => {
          (grid as HTMLElement).style.setProperty('display', 'none', 'important');
        });
      });
    };

    // Initial setup
    preserveScrollbarsAndApplyFixes();

    // Monitor for any changes to body styles
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          // If someone tries to set overflow to hidden or add padding-right, override it
          const target = mutation.target as HTMLElement;
          if (target === document.body || target === document.documentElement) {
            setTimeout(preserveScrollbarsAndApplyFixes, 0);
          }
        }
      });
    });

    // Observe both body and html elements
    observer.observe(document.body, { 
      attributes: true, 
      attributeFilter: ['style', 'class'] 
    });
    
    observer.observe(document.documentElement, { 
      attributes: true, 
      attributeFilter: ['style', 'class'] 
    });

    // Periodic checks to ensure scrollbars remain
    const interval = setInterval(preserveScrollbarsAndApplyFixes, 100);

    // Event listeners for modal close events
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setTimeout(preserveScrollbarsAndApplyFixes, 10);
      }
    };

    const handleClick = (_event: MouseEvent) => {
      // Force preserve scrollbars on any click
      setTimeout(preserveScrollbarsAndApplyFixes, 10);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('click', handleClick, true); // Use capture phase

    // Cleanup
    return () => {
      observer.disconnect();
      clearInterval(interval);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleClick, true);
      // Restore original styles
      document.body.style.overflow = originalStyles.bodyOverflow;
      document.body.style.paddingRight = originalStyles.bodyPaddingRight;
      document.documentElement.style.overflow = originalStyles.htmlOverflow;
    };
  }, []);

  return (
    <span style={{ color: '#ffffff' }}>
      {React.createElement(SidebarSearchModal)}
    </span>
  );
};
